import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiOg } from './client';
const ADMIN_PATH = '/api/v1/admin';
const ADMIN_KEY = ['og', 'locations-admin'];
const idPath = (value) => encodeURIComponent(String(value));
function refreshAdminCatalog(queryClient) {
    return queryClient.invalidateQueries({ queryKey: ADMIN_KEY }, { throwOnError: true });
}
function refreshAdminCatalogInBackground(queryClient) {
    void queryClient.invalidateQueries({ queryKey: ADMIN_KEY });
}
export function useAdminLocations(status = 'all') {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'locations', status],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/locations?status=${status}`),
    });
}
export function useAdminLocationCard(id, options = {}) {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'location-card', id],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/locations/${idPath(id)}/card`),
        enabled: id != null && (options.enabled ?? true),
    });
}
export function useAdminLocationGroups() {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'location-groups'],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/location-groups`),
    });
}
export function useAdminLocationTranslations(id) {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'location-translations', id],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/locations/${idPath(id)}/translations`),
        enabled: id != null,
    });
}
export function useAdminLocationGroupTranslations(id) {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'location-group-translations', id],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/location-groups/${idPath(id)}/translations`),
        enabled: id != null,
    });
}
export function useAdminReferenceOptionTranslations(id) {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'reference-option-translations', id],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/options/${idPath(id)}/translations`),
        enabled: id != null,
    });
}
export function useAdminLocationOption(id, options = {}) {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'location-option', id],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/location-options/${idPath(id)}`),
        enabled: id != null && (options.enabled ?? true),
    });
}
export function useAdminReferenceOptions() {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'reference-options'],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/options`),
    });
}
export function useAdminSquads(status = 'all') {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'squads', status],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/squads?status=${status}`),
    });
}
export function useAdminLocationOperations({ limit = 20, offset = 0 } = {}) {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'operations', limit, offset],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/operations?limit=${limit}&offset=${offset}`),
    });
}
export function useAdminLocationOperation(id, options = {}) {
    return useQuery({
        queryKey: [...ADMIN_KEY, 'operation', id],
        queryFn: () => apiOg('locations', `${ADMIN_PATH}/operations/${idPath(id)}`),
        enabled: Boolean(id) && (options.enabled ?? true),
        refetchInterval: (query) => (query.state.data?.status === 'running' ? 3000 : false),
        refetchIntervalInBackground: false,
    });
}
export function useUpdateAdminLocation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('locations', `${ADMIN_PATH}/locations/${idPath(id)}`, {
            method: 'PATCH',
            body: patch,
        }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
export function useCreateAdminLocation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('locations', `${ADMIN_PATH}/locations`, { method: 'POST', body }),
        onSuccess: () => refreshAdminCatalogInBackground(queryClient),
    });
}
export function useCreateAdminLocationGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('locations', `${ADMIN_PATH}/location-groups`, { method: 'POST', body }),
        onSuccess: () => refreshAdminCatalogInBackground(queryClient),
    });
}
export function useUpdateAdminLocationGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('locations', `${ADMIN_PATH}/location-groups/${idPath(id)}`, {
            method: 'PATCH',
            body: patch,
        }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
export function useUpdateAdminReferenceOption() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('locations', `${ADMIN_PATH}/options/${idPath(id)}`, {
            method: 'PATCH',
            body: patch,
        }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
export function useCreateAdminReferenceOption() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('locations', `${ADMIN_PATH}/options`, { method: 'POST', body }),
        onSuccess: () => refreshAdminCatalogInBackground(queryClient),
    });
}
export function useUpdateAdminLocationOption() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('locations', `${ADMIN_PATH}/location-options/${idPath(id)}`, {
            method: 'PATCH',
            body: patch,
        }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
export function useCreateAdminLocationOption() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('locations', `${ADMIN_PATH}/location-options`, { method: 'POST', body }),
        onSuccess: () => refreshAdminCatalogInBackground(queryClient),
    });
}
export function useCreateAdminSquad() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('locations', `${ADMIN_PATH}/squads`, { method: 'POST', body }),
        onSuccess: () => refreshAdminCatalogInBackground(queryClient),
    });
}
export function useUpdateAdminSquad() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('locations', `${ADMIN_PATH}/squads/${idPath(id)}`, {
            method: 'PATCH',
            body: patch,
        }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
export function useAttachAdminSquad() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ locationOptionId, squadId }) => apiOg('locations', `${ADMIN_PATH}/location-options/${idPath(locationOptionId)}/squads`, { method: 'POST', body: { squad_id: squadId } }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
export function useDetachAdminSquad() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ locationOptionId, squadId }) => apiOg('locations', `${ADMIN_PATH}/location-options/${idPath(locationOptionId)}/squads/${idPath(squadId)}`, { method: 'DELETE' }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
function useDeleteHook(path) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('locations', `${ADMIN_PATH}/${path}/${idPath(id)}`, { method: 'DELETE' }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
export function useDeleteAdminLocation() {
    return useDeleteHook('locations');
}
export function useDeleteAdminLocationGroup() {
    return useDeleteHook('location-groups');
}
export function useDeleteAdminLocationOption() {
    return useDeleteHook('location-options');
}
export function useDeleteAdminReferenceOption() {
    return useDeleteHook('options');
}
export function useDeleteAdminSquad() {
    return useDeleteHook('squads');
}
function useSaveTranslation(entity) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, body }) => apiOg('locations', `${ADMIN_PATH}/${entity}/${idPath(id)}/translations`, {
            method: 'PUT',
            body,
        }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
function useDeleteTranslation(entity) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, language }) => apiOg('locations', `${ADMIN_PATH}/${entity}/${idPath(id)}/translations/${idPath(language)}`, { method: 'DELETE' }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
export function useSaveAdminLocationTranslation() {
    return useSaveTranslation('locations');
}
export function useSaveAdminLocationGroupTranslation() {
    return useSaveTranslation('location-groups');
}
export function useSaveAdminReferenceOptionTranslation() {
    return useSaveTranslation('options');
}
export function useDeleteAdminLocationTranslation() {
    return useDeleteTranslation('locations');
}
export function useDeleteAdminLocationGroupTranslation() {
    return useDeleteTranslation('location-groups');
}
export function useDeleteAdminReferenceOptionTranslation() {
    return useDeleteTranslation('options');
}
export function useUnassignAdminLocationUsers() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('locations', `${ADMIN_PATH}/locations/${idPath(id)}/unassign-users`, {
            method: 'POST',
        }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
export function useUnassignAdminLocationOptionUsers() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('locations', `${ADMIN_PATH}/location-options/${idPath(id)}/unassign-users`, { method: 'POST' }),
        onSuccess: () => refreshAdminCatalog(queryClient),
    });
}
// Compatibility wrappers for the former standalone option/squad screens and dashboard.
export function useOgOptions() {
    return useAdminReferenceOptions();
}
export function useCreateOgOption() {
    return useCreateAdminReferenceOption();
}
export function useUpdateOgOption() {
    return useUpdateAdminReferenceOption();
}
export function useDeleteOgOption() {
    return useDeleteAdminReferenceOption();
}
export function useOgSquads() {
    return useAdminSquads('all');
}
export function useCreateOgSquad() {
    return useCreateAdminSquad();
}
export function useUpdateOgSquad() {
    return useUpdateAdminSquad();
}
export function useDeleteOgSquad() {
    return useDeleteAdminSquad();
}
export function useClearCache() {
    return useMutation({
        mutationFn: (target) => apiOg('locations', `${ADMIN_PATH}/cache/${target}`, { method: 'DELETE' }),
    });
}
export function useClearUserCache() {
    return useMutation({
        mutationFn: (globalauthId) => apiOg('locations', `${ADMIN_PATH}/cache/user/${encodeURIComponent(globalauthId)}`, {
            method: 'DELETE',
        }),
    });
}
